'use client';

import React, { forwardRef, useState } from 'react';
import { Alert, Table } from '@/shared/components';
import { ListTitle } from './list-title';
import { ListImage } from './list-image';
import { ListPrice } from './list-price';
import dayjs from 'dayjs';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { TableColumn } from '@/shared/components/Table/use-column';
import styled from '@emotion/styled';
import { Trans } from 'react-i18next';
import Link from 'next/link';
import { CURRENCY } from '@/app/utils/constant';
import { AddCartFailMessage } from '@/shared/pageComponents/add-cart-fail-message';
import { usePageTracking } from '@/shared/tracking/use-tracking';
import { usePlacement } from '@/shared/tracking/use-tracking-helpers';
import { getContactUsLink } from '@/app/utils/get-contact-us-link';

const StyledTableBox = styled('div')`
  & .c-table td {
    border: none;
  }
  & .c-table tr:has(.open-expand-row) {
    background-color: white;
  }
  & .c-table .open-expand-row {
    height: fit-content;
    padding: 0px;
    margin: 0px;
  }
`;

interface IProps {
  data: any;
  selectFunc: any;
  removeFunc: any;
  rowSelection: any;
  setRowSelection: any;
}
const FavoritesTable = forwardRef<HTMLInputElement, IProps>(function FavoritesTable(props, ref) {
  const { data, selectFunc, removeFunc, rowSelection, setRowSelection } = props;
  const [addCartFailMessage, setAddCartFailMessage] = useState([]);
  const { t: commonT } = useLang(NAMESPACE.common);
  const { t } = useLang(NAMESPACE.favorites);
  const columns: TableColumn<any>[] = [
    {
      accessorKey: 'itemImageUrlKey',
      header: '-',
      cell: (row) => {
        return <ListImage product={row} />;
      },
    },
    {
      accessorKey: 'catalogName',
      header: t('item'),
      cell: (row, rowIndex) => {
        return (
          <ListTitle
            product={row}
            removeFunc={removeFunc}
            setAddCartFailMessage={setAddCartFailMessage}
            index={rowIndex}
          />
        );
      },
    },
    {
      accessorKey: 'catalogNumber',
      size: 140,
      header: commonT('catalog.number'),
    },
    {
      accessorKey: 'unitSize',
      header: t('unit.size'),
      style: {
        minWidth: '125px',
      },
    },
    {
      accessorKey: 'addDate',
      header: t('add.date'),
      cell: (row) => {
        const dateVal = row.addDate;
        const date = dateVal ? dayjs(dateVal).format('DD MMM YYYY') : '';
        return date;
      },
      style: {
        minWidth: '125px',
      },
    },
    {
      accessorKey: 'listPrice',
      header: t('pricing') + '(' + CURRENCY + ')',
      cell: (row) => {
        return <ListPrice product={row} />;
      },
      style: {
        minWidth: '200px',
      },
    },
  ];
  const placement = usePlacement();
  usePageTracking(
    {
      key: 'productImpressions',
      payload: data?.map((product) => {
        return {
          name: product.catalogName,
          catalogId: product.catalogNumber,
          placement: placement,
        };
      }),
    },
    data ? true : false
  );
  return (
    <StyledTableBox>
      <Table
        openExpand
        expandable={{
          expandedRowRender: (row) => (
            <ExpandInfo
              row={row}
              addCartFailMessage={addCartFailMessage}
              setAddCartFailMessage={setAddCartFailMessage}
            />
          ),
          rowExpandable: (row) => false,
        }}
        columns={columns}
        dataSource={data}
        enableRowSelection={{}}
        className="w-full favorites-table min-w-[1300px]"
        noDataDes={t('no.favorites.found')}
        showDropDown={false}
        selectFunc={selectFunc}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
      />
    </StyledTableBox>
  );
});

function ExpandInfo({ row, addCartFailMessage, setAddCartFailMessage }) {
  const { t: commonT } = useLang(NAMESPACE.common);
  const { t, lang } = useLang(NAMESPACE.favorites);

  const addCartFailAlertOpen = () => {
    const matchingItem = addCartFailMessage.find((item) => item.catalogNumber === row.catalogNumber);
    if (matchingItem) {
      return true;
    }
    return false;
  };

  return (
    <>
      <Alert
        className="m-2"
        type="warning"
        open={row?.warningMessageStatus === 'DISCONTINUED'}
        content={
          <Trans
            t={commonT}
            i18nKey="discontinued.tips"
            components={[<Link target="_blank" href={getContactUsLink(lang)} key="technical_link"></Link>]}
            values={{ sku: row?.catalogNumber }}
          />
        }
      />
      <Alert
        className="m-2"
        type="warning"
        open={row?.warningMessageStatus === 'BLOCKED'}
        content={
          <Trans
            t={commonT}
            i18nKey="not.web.orderable.tips"
            components={[<Link target="_blank" href={getContactUsLink(lang)} key="custom_service_link"></Link>]}
            values={{ sku: row?.catalogNumber }}
          />
        }
      />
      <Alert className="m-2" type="warning" open={row?.dwgDepletedStockFlag} content={t('longer.available.tips')} />
      <AddCartFailMessage
        className="m-2"
        failList={addCartFailMessage}
        open={addCartFailAlertOpen()}
        onClose={() => setAddCartFailMessage([])}
      />
    </>
  );
}

export { FavoritesTable };
